import { call, put, takeEvery } from "redux-saga/effects";
import SweetAlert from "../../../../components/sweetalert/SweetAlert";

import * as types from "./editSiteProfileActionType";
import * as actions from "./editSiteProfileActions";
import * as services from "./editSiteProfileServices";
import { downloadBase64File } from "../../../../utils/fileDownloads";
import * as commonActions from "../../../common/services/commonActions";

// Fetch Site Details Saga
function* fetchSiteDetailsSaga(action) {
  try {
    const { siteId } = action.payload;
    const response = yield call(services.getSiteDetailsById, siteId);

    if (response?.success) {
      yield put(actions.fetchSiteDetailsSuccess(response));
    } else {
      yield put(
        actions.fetchSiteDetailsFailure("Failed to fetch facility details"),
      );
    }
  } catch (error) {
    const errorMessage = "Failed to fetch facility details";
    yield put(actions.fetchSiteDetailsFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Fetch Zipcode Details Saga
function* fetchZipcodeDetailsSaga(action) {
  try {
    const { zipcode } = action.payload;
    const response = yield call(services.getZipcodeDetailsByZipcode, zipcode);

    if (response?.success) {
      yield put(actions.fetchZipcodeDetailsSuccess(response));
    } else {
      yield put(
        actions.fetchZipcodeDetailsFailure("Failed to fetch zipcode details"),
      );
    }
  } catch (error) {
    const errorMessage = "Failed to fetch zipcode details";
    yield put(actions.fetchZipcodeDetailsFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Fetch Site Alias History Saga
function* fetchSiteAliasHistorySaga(action) {
  try {
    const { siteAliasDetailId } = action.payload;
    const response = yield call(
      services.getSiteAliasHistoryById,
      siteAliasDetailId,
    );

    if (response?.success) {
      yield put(actions.fetchSiteAliasHistorySuccess(response));
    } else {
      yield put(
        actions.fetchSiteAliasHistoryFailure(
          "Failed to fetch facility alias history",
        ),
      );
    }
  } catch (error) {
    const errorMessage = "Failed to fetch facility alias history";
    yield put(actions.fetchSiteAliasHistoryFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Fetch Sister Site History Saga
function* fetchSisterSiteHistorySaga(action) {
  try {
    const { sisterSiteId } = action.payload;
    const response = yield call(services.getSisterSiteHistory, sisterSiteId);

    if (response?.success) {
      yield put(
        actions.fetchSisterSiteHistorySuccess(response.values || response),
      );
    } else {
      yield put(
        actions.fetchSisterSiteHistoryFailure(
          "Failed to fetch sister facility history",
        ),
      );
    }
  } catch (error) {
    const errorMessage = "Failed to fetch sister facility history";
    yield put(actions.fetchSisterSiteHistoryFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Save Site Details Saga
function* saveSiteDetailsSaga(action) {
  try {
    const response = yield call(services.saveSiteDetails, action.payload);

    if (response?.success) {
      yield put(actions.saveSiteDetailsSuccess(response));
      yield put(commonActions.assignedSitesCountRequest());
      yield put(commonActions.unassignedSitesCountRequest());
      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Facility details saved successfully!",
      });
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save provider schedule";
      yield put(
        actions.saveSiteDetailsFailure("Failed to save facility details"),
      );
      SweetAlert.error({ title: "Error!", text: errorMessage });
    }
  } catch (error) {
    const errorMessage = "Failed to save facility details";
    yield put(actions.saveSiteDetailsFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Save Priority SLA Saga
function* savePrioritySLASaga(action) {
  try {
    const response = yield call(services.savePrioritySla, action.payload);

    if (response?.success) {
      yield put(actions.savePrioritySlaSuccess(response));
      yield put(actions.fetchSiteDetailsRequest(action.payload.siteID));

      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Priority SLA saved successfully!",
      });
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save priority SLA";
      yield put(actions.savePrioritySlaFailure(errorMessage));
      SweetAlert.error({ title: "Error!", text: errorMessage });
    }
  } catch (error) {
    const errorMessage = "Failed to save priority SLA";
    yield put(actions.savePrioritySlaFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Save Site Tags Saga
function* saveSiteTagsSaga(action) {
  try {
    const response = yield call(services.saveSiteTags, action.payload);

    if (response?.success) {
      yield put(actions.saveSiteTagsSuccess(response));

      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Tags updated successfully!",
      });

      if (typeof action.onSuccess === "function") {
        action.onSuccess(response);
      }
    } else {
      const errorMessage = response?.errorMessage || "Failed to save tags";
      yield put(actions.saveSiteTagsFailure(errorMessage));
      SweetAlert.error({ title: "Error", text: errorMessage });
    }
  } catch (error) {
    const errorMessage = "Failed to save tags";
    yield put(actions.saveSiteTagsFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Fetch Sister Sites Saga
function* fetchSisterSitesSaga(action) {
  try {
    const response = yield call(
      services.getSisterSitesBySiteId,
      action.payload.siteId,
    );

    const payload = response?.values || response || { sisterSitesList: [] };
    yield put(actions.fetchSisterSitesSuccess(payload));
  } catch (error) {
    const errorMessage = "Failed to fetch sister facilities";
    yield put(actions.fetchSisterSitesFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Enable / Disable Sister Site Saga
function* enableDisableSisterSiteSaga(action) {
  try {
    const response = yield call(
      services.enableDisableSisterSite,
      action.payload,
    );

    if (response?.success) {
      yield put(actions.enableDisableSisterSiteSuccess(response));

      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Operation completed successfully",
      });

      const siteId =
        action.payload.SiteID ||
        action.payload.siteId ||
        action.payload.SiteId ||
        "";

      if (siteId) {
        yield put(actions.fetchSisterSitesRequest(siteId));
      }
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to update sister facility";
      yield put(actions.enableDisableSisterSiteFailure(errorMessage));
      SweetAlert.error({ title: "Error", text: errorMessage });
    }
  } catch (error) {
    const errorMessage = "Failed to update sister facility";
    yield put(actions.enableDisableSisterSiteFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Fetch Site Alias Saga
function* fetchSiteAliasSaga(action) {
  try {
    const response = yield call(
      services.getSiteAliasBySiteId,
      action.payload.siteId,
    );

    yield put(actions.fetchSiteAliasSuccess(response || []));
  } catch (error) {
    const errorMessage = "Failed to fetch facility alias";
    yield put(actions.fetchSiteAliasFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Save Site Alias Saga
function* saveSiteAliasSaga(action) {
  try {
    const response = yield call(services.saveSiteAliasDetail, action.payload);

    if (response?.success) {
      yield put(actions.saveSiteAliasSuccess(response));
      yield put(actions.fetchSiteAliasRequest(action.payload.SiteID));

      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Facility alias saved successfully!",
      });
      if (typeof action.onSuccess === "function") {
        action.onSuccess(response);
      }
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save facility alias";
      yield put(actions.saveSiteAliasFailure(errorMessage));
      SweetAlert.error({ title: "Error!", text: errorMessage });
    }
  } catch (error) {
    const errorMessage = "Failed to save facility alias";
    yield put(actions.saveSiteAliasFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Download Site Model Input Template Saga
function* downloadSiteModelInputTemplateSaga(action) {
  try {
    const response = yield call(
      services.downloadSiteModelInputTemplate,
      action.payload.siteModelInputFileName,
    );

    if (response?.success && response.values) {
      yield call(
        downloadBase64File,
        response.values,
        "FacilityModelInput",
        "FacilityModelInputTemplate.xlsx",
      );

      yield put(actions.downloadSiteModelInputTemplateSuccess(response));

      SweetAlert.success({
        title: "Success!",
        text: response?.resultMessage || "Template downloaded successfully!",
      });
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to download template";
      yield put(actions.downloadSiteModelInputTemplateFailure(errorMessage));
      SweetAlert.error({ title: "Error", text: errorMessage });
    }
  } catch (error) {
    const errorMessage = "Failed to download template";
    yield put(actions.downloadSiteModelInputTemplateFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Fetch Site Onboarding Details Saga
function* fetchSiteOnboardingDetailsSaga(action) {
  try {
    const { SiteOnboardingID } = action.payload;
    const response = yield call(
      services.getSiteOnboardingDetailsByID,
      SiteOnboardingID,
    );

    if (response?.success) {
      yield put(actions.fetchSiteOnboardingDetailsSuccess(response));
    } else {
      yield put(
        actions.fetchSiteOnboardingDetailsFailure(
          "Failed to fetch facility onboarding details",
        ),
      );
    }
  } catch (error) {
    const errorMessage = "Failed to fetch facility onboarding details";
    yield put(actions.fetchSiteOnboardingDetailsFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}
// Fetch Model Input Files Saga
function* fetchModelInputFilesSaga(action) {
  try {
    const { siteId } = action.payload;

    const response = yield call(services.getAllSiteModelInputFiles, siteId);

    // Always dispatch success, let the component handle empty data
    yield put(actions.fetchModelInputFilesSuccess(response || []));
  } catch (error) {
    yield put(
      actions.fetchModelInputFilesFailure(`Exception: ${error.message}`),
    );
  }
}
// Fetch Site Model Filter Saga (Overall Priority / Modality Mix)
function* fetchSiteModelFilterSaga(action) {
  try {
    const payload = action.payload;

    const response = yield call(services.getAllSiteModelFilter, payload);

    if (response?.success) {
      // dispatch only the values object to keep reducer payload small
      yield put(actions.fetchSiteModelFilterSuccess(response.values || {}));
    } else {
      yield put(
        actions.fetchSiteModelFilterFailure("API returned success=false"),
      );
    }
  } catch (error) {
    yield put(
      actions.fetchSiteModelFilterFailure(`Exception: ${error.message}`),
    );
  }
}
function* uploadSiteModelInputSaga(action) {
  try {
    const { file, siteId, createdBy } = action.payload;

    // if (!file) {
    //     const errorMsg = 'File is required for upload';
    //     yield put(actions.uploadSiteModelInputFailure(errorMsg));
    //     SweetAlert.error('Error', errorMsg, 'error');
    //     return;
    // }

    const response = yield call(
      services.uploadSiteModelInputFile,
      file,
      siteId,
      createdBy,
    );

    if (response?.success) {
      yield put(actions.uploadSiteModelInputSuccess(response));

      SweetAlert.error({
        icon: "success",
        title: "Success",
        text: response?.resultMessage || "File uploaded successfully",
        timer: 2000,
        showConfirmButton: true,
      });

      // Refresh model input files list
      yield put(actions.fetchModelInputFilesRequest(siteId));
      yield put(
        actions.checkPrioritySubspecialityConfigBySiteIdRequest({
          SiteID: Number(siteId),
        }),
      );
      // Refresh Priority/Subspeciality Split tab data
      yield put(actions.fetchPrioritySubspecialitySplitRequest(siteId));
      // Refresh site model filter data
      yield put(
        actions.fetchSiteModelFilterRequest({
          SiteID: String(siteId),
          ExcludedUnreadDates: "",
          ExcludedReadDates: "",
          ExcludedPriorities: "",
          ExcludedModalities: "",
          ExcludedSubSpecialities: "",
          IsClear: false,
          IsFilterApplied: false,
          CreatedBy: createdBy || 0,
        }),
      );
    } else {
      yield put(
        actions.uploadSiteModelInputFailure(
          response?.errorMessage || "Upload failed",
        ),
      );
      SweetAlert.error({
        title: "Error",
        text: response?.errorMessage || "Failed to upload file",
      });
    }
  } catch (error) {
    yield put(
      actions.uploadSiteModelInputFailure(`Exception: ${error.message}`),
    );
    SweetAlert.error({
      title: "Error",
      text: error?.message || "Failed to upload file",
    });
  }
}

// Save Site Model Input Saga
function* saveSiteModelInputSaga(action) {
  try {
    const response = yield call(services.saveSiteModelInput, action.payload);

    if (response?.success) {
      yield put(actions.saveSiteModelInputSuccess(response));

      // Refresh model input files and filters after successful save
      const siteId =
        action.payload?.SiteID ||
        action.payload?.siteID ||
        action.payload?.SiteId ||
        action.payload?.siteId ||
        "";
      if (siteId) {
        yield put(actions.fetchModelInputFilesRequest(siteId));
        yield put(
          actions.checkPrioritySubspecialityConfigBySiteIdRequest({
            SiteID: Number(siteId),
          }),
        );
        // refetch filters to reflect saved changes
        yield put(
          actions.fetchSiteModelFilterRequest({
            SiteID: String(siteId),
            ExcludedUnreadDates: "",
            ExcludedReadDates: "",
            ExcludedPriorities: "",
            ExcludedModalities: "",
            ExcludedSubSpecialities: "",
            IsClear: false,
            IsFilterApplied: false,
            CreatedBy: action.payload?.CreatedBy || 0,
          }),
        );
      }

      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage || "Facility model input saved successfully!",
      });
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to save facility model input";
      yield put(actions.saveSiteModelInputFailure(errorMessage));
      SweetAlert.error({ title: "Error", text: errorMessage });
    }
  } catch (error) {
    const errorMessage = "Failed to save facility model input";
    yield put(actions.saveSiteModelInputFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Fetch Priority / Subspeciality Split Saga
function* fetchPrioritySubspecialitySplitSaga(action) {
  try {
    const { siteId } = action.payload;
    const response = yield call(
      services.getPrioritySubspecialitySplitBySiteId,
      siteId,
    );

    if (response?.success) {
      yield put(actions.fetchPrioritySubspecialitySplitSuccess(response));
    } else {
      const errorMessage =
        response?.errorMessage ||
        "Failed to fetch priority / subspeciality split data";
      yield put(actions.fetchPrioritySubspecialitySplitFailure(errorMessage));
      SweetAlert.error({ title: "Error", text: errorMessage });
    }
  } catch (error) {
    const errorMessage = "Failed to fetch priority / subspeciality split data";
    yield put(actions.fetchPrioritySubspecialitySplitFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Check Priority / Subspeciality Config Availability Saga
function* checkPrioritySubspecialityConfigBySiteIdSaga(action) {
  try {
    const response = yield call(
      services.checkPrioritySubspecialityConfigBySiteId,
      action.payload,
    );

    if (response?.success) {
      yield put(
        actions.checkPrioritySubspecialityConfigBySiteIdSuccess(
          response.values || {},
        ),
      );
    } else {
      const errorMessage =
        response?.errorMessage || "Failed to check facility info availability";
      yield put(
        actions.checkPrioritySubspecialityConfigBySiteIdFailure(errorMessage),
      );
    }
  } catch (error) {
    yield put(
      actions.checkPrioritySubspecialityConfigBySiteIdFailure(
        error?.message || "Failed to check facility info availability",
      ),
    );
  }
}

// Save Priority / Subspeciality Split Saga
function* savePrioritySubspecialitySplitSaga(action) {
  try {
    const response = yield call(
      services.savePrioritySubspecialitySplits,
      action.payload,
    );

    if (response?.success) {
      yield put(actions.savePrioritySubspecialitySplitSuccess(response));

      const siteId =
        action.payload?.SiteId ||
        action.payload?.SiteID ||
        action.payload?.siteId ||
        action.payload?.siteID ||
        "";

      if (siteId) {
        yield put(actions.fetchPrioritySubspecialitySplitRequest(siteId));
        yield put(
          actions.checkPrioritySubspecialityConfigBySiteIdRequest({
            SiteID: Number(siteId),
          }),
        );
        yield put(
          actions.fetchSiteModelFilterRequest({
            SiteID: String(siteId),
            ExcludedUnreadDates: "",
            ExcludedReadDates: "",
            ExcludedPriorities: "",
            ExcludedModalities: "",
            ExcludedSubSpecialities: "",
            IsClear: false,
            IsFilterApplied: false,
            CreatedBy: action.payload?.CreatedBy || 0,
          }),
        );
      }

      SweetAlert.success({
        title: "Success!",
        text:
          response?.resultMessage ||
          "Priority / subspeciality split saved successfully!",
      });
    } else {
      const errorMessage =
        response?.errorMessage ||
        "Failed to save priority / subspeciality split";
      yield put(actions.savePrioritySubspecialitySplitFailure(errorMessage));
      SweetAlert.error({ title: "Error", text: errorMessage });
    }
  } catch (error) {
    const errorMessage = "Failed to save priority / subspeciality split";
    yield put(actions.savePrioritySubspecialitySplitFailure(errorMessage));
    SweetAlert.error({ title: "Error", text: errorMessage });
  }
}

// Fetch State List Saga
function* fetchStateListSaga() {
  try {
    const response = yield call(services.getStateList);
    yield put(actions.fetchStateListSuccess(response?.value || []));
  } catch (error) {
    yield put(
      actions.fetchStateListFailure(error.message || "Failed to fetch states"),
    );
  }
}

// Update Site Scheduling State Saga
function* updateSiteStateSaga(action) {
  try {
    const { siteId, stateId } = action.payload;
    const response = yield call(services.updateSiteState, siteId, stateId);
    if (response?.success) {
      yield put(actions.updateSiteStateSuccess());
    } else {
      yield put(
        actions.updateSiteStateFailure(
          response?.errorMessage || "Failed to update state",
        ),
      );
      SweetAlert.error({
        title: "Error",
        text: response?.errorMessage || "Failed to update facility state.",
      });
    }
  } catch (error) {
    yield put(
      actions.updateSiteStateFailure(error.message || "Failed to update state"),
    );
    SweetAlert.error({
      title: "Error",
      text: error.message || "Failed to update facility state.",
    });
  }
}

// Watcher Saga
export default function* editSiteProfileSaga() {
  yield takeEvery(types.FETCH_SITE_DETAILS_REQUEST, fetchSiteDetailsSaga);
  yield takeEvery(types.FETCH_ZIPCODE_DETAILS_REQUEST, fetchZipcodeDetailsSaga);
  yield takeEvery(
    types.FETCH_SITE_ALIAS_HISTORY_REQUEST,
    fetchSiteAliasHistorySaga,
  );
  yield takeEvery(
    types.FETCH_SISTER_SITE_HISTORY_REQUEST,
    fetchSisterSiteHistorySaga,
  );
  yield takeEvery(types.SAVE_SITE_DETAILS_REQUEST, saveSiteDetailsSaga);
  yield takeEvery(types.SAVE_PRIORITY_SLA_REQUEST, savePrioritySLASaga);
  yield takeEvery(types.SAVE_SITE_TAGS_REQUEST, saveSiteTagsSaga);
  yield takeEvery(types.FETCH_SISTER_SITES_REQUEST, fetchSisterSitesSaga);
  yield takeEvery(
    types.ENABLE_DISABLE_SISTER_SITE_REQUEST,
    enableDisableSisterSiteSaga,
  );
  yield takeEvery(types.FETCH_SITE_ALIAS_REQUEST, fetchSiteAliasSaga);
  yield takeEvery(types.SAVE_SITE_ALIAS_REQUEST, saveSiteAliasSaga);
  yield takeEvery(
    types.FETCH_MODEL_INPUT_FILES_REQUEST,
    fetchModelInputFilesSaga,
  );
  yield takeEvery(
    types.FETCH_SITE_MODEL_FILTER_REQUEST,
    fetchSiteModelFilterSaga,
  );
  yield takeEvery(
    types.SITE_MODEL_INPUT_UPLOAD_REQUEST,
    uploadSiteModelInputSaga,
  );
  yield takeEvery(types.SAVE_SITE_MODEL_INPUT_REQUEST, saveSiteModelInputSaga);
  yield takeEvery(
    types.DOWNLOAD_SITE_MODEL_INPUT_TEMPLATE_REQUEST,
    downloadSiteModelInputTemplateSaga,
  );
  yield takeEvery(
    types.FETCH_PRIORITY_SUBSPECIALITY_SPLIT_REQUEST,
    fetchPrioritySubspecialitySplitSaga,
  );
  yield takeEvery(
    types.CHECK_PRIORITY_SUBSPECIALITY_CONFIG_BY_SITEID_REQUEST,
    checkPrioritySubspecialityConfigBySiteIdSaga,
  );
  yield takeEvery(
    types.SAVE_PRIORITY_SUBSPECIALITY_SPLIT_REQUEST,
    savePrioritySubspecialitySplitSaga,
  );

  //Get Site Onboarding Details Watcher
  yield takeEvery(
    types.FETCH_SITE_ONBOARDING_DETAILS_REQUEST,
    fetchSiteOnboardingDetailsSaga,
  );

  yield takeEvery(types.FETCH_STATE_LIST_REQUEST, fetchStateListSaga);
  yield takeEvery(types.UPDATE_SITE_STATE_REQUEST, updateSiteStateSaga);
}
